import { useMemo } from 'react';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useUser } from '@/queries/useUser';
import { useAutomations } from '@/features/automations/queries';
import { useAutomationsPageLogic, useInstagramIntegration } from '@/features/automations/hooks';
import { AutomationsList } from '@/features/automations/components/AutomationsList';
import { AutomationHeader } from '@/features/automations/components/AutomationHeader';
import { AllSetState, InitialEmptyState } from '@/features/automations/components/EmptyStates';
import { useAutomationsStore } from '@/features/automations/stores';
import { DEFAULT_AUTOMATION_DATA } from '@/features/automations/constants';
import type { Automation } from '@/features/automations/types/automation.types';
import { configSettings } from '@/lib';

const AutomationsPage = () => {
    useBreadcrumbs([{ title: 'My AutoDM', to: '/automations', id: 'automations', active: true }]);

    const { data: automations = [], isLoading: isLoadingAutomations, isRefetching } = useAutomations();
    const { user, isUserLoading } = useUser();
    const {
        instagramStatus,
        igUsername,
        refetchIntegrations,
        isLoading: isLoadingInstagram,
    } = useInstagramIntegration();
    const { allSetAcknowledged, handleAcknowledge, handleCreateAutomation } = useAutomationsPageLogic();

    // Store state for unsaved automation
    const setUnsavedAutomation = useAutomationsStore(state => state.setUnsavedAutomation);

    // Handler to add a new unsaved automation
    const handleAddAutomation = () => {
        const newUnsavedAutomation: Automation = {
            ...DEFAULT_AUTOMATION_DATA,
            automation_id: undefined as any, // undefined until saved
            user_id: 0, // Temporary, will be set on save
            created_at: '', // Empty until saved
        };
        newUnsavedAutomation.data.response.data.content = newUnsavedAutomation.data.response.data.content.replace(
            '<user_store_url>',
            `${configSettings.frontDomain}${user?.username}`
        );
        setUnsavedAutomation(newUnsavedAutomation);

        // Scroll to top and open the new automation after a brief delay
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    };

    // Check if user has active subscription
    const hasActiveSubscription = useMemo(() => {
        if (!user?.subscriptions) return false;
        return Object.values(user.subscriptions).some(subscription => subscription.status === 1);
    }, [user?.subscriptions]);

    const isInitialLoading = (isLoadingAutomations && !isRefetching) || isUserLoading || isLoadingInstagram;

    // Empty state: Show when no automations OR no active subscription (but only after loading is complete)
    if (!isInitialLoading && (automations.length === 0 || !hasActiveSubscription)) {
        // Show "You're All Set!" if just connected Instagram and haven't acknowledged yet
        if (instagramStatus === 'connected' && !allSetAcknowledged && hasActiveSubscription) {
            return (
                <div className='mx-auto w-full max-w-screen-2xl pb-6'>
                    <AllSetState onAcknowledge={handleAcknowledge} />
                </div>
            );
        }

        // Show initial empty state (handles subscription, connection, and create states)
        return (
            <div className='mx-auto w-full max-w-screen-2xl pb-6'>
                <InitialEmptyState
                    instagramStatus={instagramStatus}
                    hasActiveSubscription={hasActiveSubscription}
                    onCreateAutomation={handleCreateAutomation}
                    onInstagramConnected={refetchIntegrations}
                />
            </div>
        );
    }

    if (isLoadingAutomations) {
        // TODO: Find a way to modify existing StanQuery to add page loader.
        return null;
    }

    // Has automations and active subscription - show list with header
    return (
        <div className='mx-auto w-full max-w-screen-2xl pb-6'>
            <AutomationHeader
                igUsername={igUsername}
                instagramStatus={instagramStatus}
                onAddAutomation={handleAddAutomation}
            />
            <AutomationsList />
        </div>
    );
};

export default AutomationsPage;
